import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { getAllLeadStatusByVisibility, ILead, updateLeadStatus } from 'wisestep-api';
import { ControlledTextInput, WsButton, WsColors, WsDialog, WsSelect } from 'wisestep-base';

import { failed, success } from '../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../utils/NotificationStore';
import { StatusList } from '../LeadDetailsForm';

const LeadStatusUpdateDialog = ({
  lead,
  onSave,
  onCancel,
}: {
  lead: ILead;
  onSave: (lead: ILead) => void;
  onCancel: () => void;
}) => {
  const [isLoading, setLoading] = useState(false);
  const [statusList, setStatusList] = useState<StatusList[]>([]);
  const formMethods = useForm();
  const setNotification = useNotificationStore((state) => state.setNotification);

  useEffect(() => {
    const getStatusList = async () => {
      const response = await getAllLeadStatusByVisibility();
      const statusData: StatusList[] = [];
      response.data?.forEach((statusObj) => {
        statusData.push({
          label: statusObj.leadStatusName as string,
          value: statusObj.id as string,
        });
      });
      setStatusList(statusData);
    };
    getStatusList();
  }, []);

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const response = await updateLeadStatus({
        id: lead.id,
        leadStatus: data?.leadStatusId
          ? { id: data.leadStatusId }
          : data?.leadStatus
          ? { id: data?.leadStatus?.id }
          : '',
        statusReason: data.statusChangeReason,
      } as ILead);
      setNotification(success('Lead Status Updated Successfully !!'));
      response.data && onSave(response.data);
    } catch {
      setNotification(failed('Something went wrong, Please try again !!'));
    }
    setLoading(false);
  };

  return (
    <WsDialog openDialog={true} onClose={onCancel}>
      <FormProvider {...formMethods}>
        <Box component="form" onSubmit={formMethods.handleSubmit(onSubmit)}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <DialogTitle>Update Status</DialogTitle>
            <IconButton
              onClick={onCancel}
              sx={{
                color: WsColors.GreyBlack,
                mr: 2,
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <Divider orientation="horizontal" />
          <DialogContent sx={{ pl: 6 }}>
            <Grid container spacing={1} width="80%">
              <Grid item xs={12}>
                <WsSelect
                  name="leadStatusId"
                  selectArray={statusList}
                  placeholder="Select Lead Status"
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <ControlledTextInput
                  name="statusChangeReason"
                  placeholder="Add Comment"
                  labelText="Comments"
                  fullWidth={true}
                  multiline
                  rows={3.5}
                  sx={{ '& .MuiInputBase-root': { height: 100 } }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <Divider orientation="horizontal" />
          <DialogActions sx={{ pr: 4, pb: 2 }}>
            <WsButton
              type="submit"
              disabled={isLoading}
              endIcon={isLoading ? <CircularProgress size={17} color="secondary" /> : <></>}
            >
              {isLoading ? 'Saving...' : 'Save'}
            </WsButton>
          </DialogActions>
        </Box>
      </FormProvider>
    </WsDialog>
  );
};

export default LeadStatusUpdateDialog;
